import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "./Dashboard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, MailCheck, BadgeCheck, Trash2, Star } from "lucide-react";

const FILTERS = ["all", "pending", "confirmed", "completed", "cancelled"];

export default function AdminBookings() {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/bookings?status_filter=${filter}`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function updateBooking(id, patch) {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, patch);
      setItems((it) => it.map((b) => (b.id === id ? data : b)));
      toast.success("Booking updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function sendReminder(id) {
    try {
      await api.post(`/admin/bookings/${id}/send-reminder`);
      toast.success("Reminder sent");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function sendFeedbackRequest(id) {
    if (!window.confirm("Email a feedback link to this client now?")) return;
    try {
      await api.post(`/admin/bookings/${id}/send-feedback-request`);
      toast.success("Feedback request sent");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      setItems((it) => it.filter((b) => b.id !== id));
      toast.success("Booking deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-8" data-testid="admin-bookings-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow mb-2">Bookings</div>
          <h1 className="font-serif text-4xl font-light">All sessions</h1>
        </div>
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-3 h-9 text-xs uppercase tracking-[0.18em] border ${filter === f ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/60"}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile: stacked cards */}
      <ul className="lg:hidden bg-card border border-border divide-y divide-border" data-testid="bookings-mobile-list">
        {loading && (<li className="px-5 py-10 text-center text-muted-foreground">Loading…</li>)}
        {!loading && items.length === 0 && (<li className="px-5 py-10 text-center text-muted-foreground">No bookings.</li>)}
        {items.map((b) => (
          <li key={b.id} className="p-4 space-y-2" data-testid={`booking-card-${b.reference}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{b.customer_name}</div>
                <div className="text-xs text-muted-foreground truncate">{b.customer_email}</div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-2 -mt-1 -mr-1 hover:bg-muted" data-testid={`booking-menu-${b.reference}`}><MoreHorizontal className="w-4 h-4" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => updateBooking(b.id, { status: "confirmed" })}>Mark confirmed</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => updateBooking(b.id, { deposit_received: true })}>Mark deposit received</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => updateBooking(b.id, { status: "completed" })}>Mark completed</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => updateBooking(b.id, { status: "cancelled" })}>Cancel</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => sendReminder(b.id)}><MailCheck className="w-4 h-4 mr-2" /> Send reminder</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => sendFeedbackRequest(b.id)}><Star className="w-4 h-4 mr-2" /> Send feedback request</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => remove(b.id)} className="text-destructive"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="text-sm">
              <div>{b.service_name}</div>
              <div className="text-xs text-muted-foreground">{b.tier_name}</div>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>{b.session_date} · {b.session_time}</span>
              <span>·</span>
              <span>${b.tier_price.toFixed(0)}</span>
              <span>·</span>
              <span className="font-mono">{b.reference}</span>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <StatusBadge value={b.status} />
              {b.deposit_received ? (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-emerald-700"><BadgeCheck className="w-3.5 h-3.5" /> Deposit paid</span>
              ) : (
                <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Deposit pending</span>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/* Desktop: full table */}
      <div className="hidden lg:block bg-card border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="bookings-table">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="px-5 py-3 eyebrow font-normal">Ref</th>
              <th className="px-5 py-3 eyebrow font-normal">Client</th>
              <th className="px-5 py-3 eyebrow font-normal">Service</th>
              <th className="px-5 py-3 eyebrow font-normal">Date</th>
              <th className="px-5 py-3 eyebrow font-normal">Total</th>
              <th className="px-5 py-3 eyebrow font-normal">Deposit</th>
              <th className="px-5 py-3 eyebrow font-normal">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={8} className="px-5 py-10 text-center text-muted-foreground">Loading…</td></tr>)}
            {!loading && items.length === 0 && (<tr><td colSpan={8} className="px-5 py-10 text-center text-muted-foreground">No bookings.</td></tr>)}
            {items.map((b) => (
              <tr key={b.id} className="border-t border-border" data-testid={`booking-row-${b.reference}`}>
                <td className="px-5 py-4 font-mono text-xs">{b.reference}</td>
                <td className="px-5 py-4">
                  <div>{b.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{b.customer_email}</div>
                </td>
                <td className="px-5 py-4">
                  <div>{b.service_name}</div>
                  <div className="text-xs text-muted-foreground">{b.tier_name}</div>
                </td>
                <td className="px-5 py-4">{b.session_date}<div className="text-xs text-muted-foreground">{b.session_time}</div></td>
                <td className="px-5 py-4">${b.tier_price.toFixed(0)}</td>
                <td className="px-5 py-4">{b.deposit_received ? <BadgeCheck className="w-4 h-4 text-emerald-600" /> : <span className="text-muted-foreground">Pending</span>}</td>
                <td className="px-5 py-4"><StatusBadge value={b.status} /></td>
                <td className="px-5 py-4 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-2 hover:bg-muted" data-testid={`booking-menu-${b.reference}`}><MoreHorizontal className="w-4 h-4" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => updateBooking(b.id, { status: "confirmed" })}>Mark confirmed</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateBooking(b.id, { deposit_received: true })}>Mark deposit received</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateBooking(b.id, { status: "completed" })}>Mark completed</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateBooking(b.id, { status: "cancelled" })}>Cancel</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => sendReminder(b.id)}><MailCheck className="w-4 h-4 mr-2" /> Send reminder</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => sendFeedbackRequest(b.id)} data-testid={`send-feedback-${b.id}`}><Star className="w-4 h-4 mr-2" /> Send feedback request</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => remove(b.id)} className="text-destructive"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
